import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AUTH_TYPE as authorizationTypes,
  getSupplierProductAuthTypes,
} from 'feature/loading-management/utils/const';
import { arrayOf, func, number, oneOfType, shape, string } from 'prop-types';
import { isNotEmpty } from 'utils/helpers/formValidations';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import { useUserRolePermissions } from 'hooks';
import FormHelperText from '@mui/material/FormHelperText';

const AuthorizationVolumes = ({ dataPath, info, setInfo }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const { isSupplier } = useUserRolePermissions();

  const originalFilteredTypes = types =>
    types.filter(
      ({ value }) =>
        value !== 'SPECIFIC' &&
        value !== 'ONETIME' &&
        value !== '1VOLDAILY' &&
        value !== '1VOLWEEKLY' &&
        value !== '1VOLMONTH'
    );

  const filteredAuthTypes = isSupplier
    ? originalFilteredTypes(getSupplierProductAuthTypes())
    : originalFilteredTypes(authorizationTypes);

  const [validation, setValidation] = useState({
    authorizationType: '',
    authorizationVolume: '',
    renewalVolume: '',
  });

  const dataLens = R.lensPath(dataPath);
  const authorizationTypeLens = R.o(dataLens, R.lensProp('authorizationType'));
  const authorizationVolumeLens = R.o(dataLens, R.lensProp('authorizationVolume'));
  const renewalVolumeLens = R.o(dataLens, R.lensProp('renewalVolume'));

  const authorizationType = R.view(authorizationTypeLens, info);
  const authorizationVolume = R.view(authorizationVolumeLens, info);
  const renewalVolume = R.view(renewalVolumeLens, info);

  // prettier-ignore
  // updateAuthorizationType :: authorizationType -> info -> info
  const updateAuthorizationType = R.curryN(2, R.compose(
      R.ifElse(
        R.propEq('authorizationType', 'ONELOAD'),
        R.assoc('authorizationVolume', 1),
        R.assoc('authorizationVolume', undefined)
      ),
      R.assoc('renewalVolume', undefined),
      R.assoc('authorizationType')
      ));

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gte(0), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const authVolCheck = ['ONELOAD', 'UNLIMITED', 'SPECIFIC', 'ONEVOL'].includes(authorizationType);
  const renewVolCheck = [
    'LIMITED',
    'ONELOAD',
    'ONETIME',
    'UNLIMITED',
    'SPECIFIC',
    'ONEVOL',
  ].includes(authorizationType);

  const isTypeMissing =
    isSupplier &&
    authorizationType &&
    !filteredAuthTypes.some(type => type.value === authorizationType);

  useEffect(() => {
    if (isTypeMissing) {
      setValidation(prev => ({
        ...prev,
        authorizationType: 'Required',
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        authorizationType: '',
      }));
    }
  }, [isTypeMissing]);

  // handleAuthorizationTypeChange :: AuthorizationType -> info
  const handleAuthorizationTypeChange = R.compose(
    setInfo,
    R.over(dataLens),
    R.tap(
      R.compose(setValidation, R.assoc('authorizationVolume', ''), R.assoc('renewalVolume', ''))
    ),
    updateAuthorizationType,
    R.path(['target', 'value'])
  );

  // handleAuthorizationVolumeChange :: event -> info
  const handleAuthorizationVolumeChange = R.compose(
    setInfo,
    R.over(dataLens),
    R.assoc('authorizationVolume'),
    R.tap(R.compose(setValidation, R.assoc('authorizationVolume'), validateVolume)),
    parseInt,
    R.path(['target', 'value'])
  );

  // handleRenewalVolumeChange :: event -> info
  const handleRenewalVolumeChange = R.compose(
    setInfo,
    R.over(dataLens),
    R.assoc('renewalVolume'),
    R.tap(R.compose(setValidation, R.assoc('renewalVolume'), validateVolume)),
    parseInt,
    R.path(['target', 'value'])
  );

  return (
    <Stack direction={lgUp ? 'row' : 'column'} spacing={2}>
      <FormControl
        error={!!validation.authorizationType && isTypeMissing}
        required
        sx={{ flexBasis: 0, flexGrow: 1 }}
      >
        <InputLabel id="authorization-type">Authorization Type</InputLabel>
        <Select
          label="Authorization Type"
          labelId="authorization-type"
          onChange={handleAuthorizationTypeChange}
          value={authorizationType || ''}
        >
          {filteredAuthTypes.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        {isTypeMissing && <FormHelperText>Required</FormHelperText>}
      </FormControl>

      <TextField
        disabled={authVolCheck}
        error={isNotEmpty(validation.authorizationVolume)}
        helperText={R.prop('authorizationVolume', validation) || 'Volume (gal)'}
        label="Authorization"
        onChange={handleAuthorizationVolumeChange}
        placeholder="--"
        required={R.not(authVolCheck)}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flexBasis: 0, flexGrow: 1 }}
        value={R.defaultTo('', authorizationVolume)}
      />

      <TextField
        disabled={renewVolCheck}
        error={isNotEmpty(validation.renewalVolume)}
        helperText={R.prop('renewalVolume', validation) || 'Volume (gal)'}
        label="Renewal"
        onChange={handleRenewalVolumeChange}
        placeholder="--"
        required={R.not(renewVolCheck)}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flexBasis: 0, flexGrow: 1 }}
        value={R.defaultTo('', renewalVolume)}
      />
    </Stack>
  );
};

AuthorizationVolumes.propTypes = {
  dataPath: arrayOf(oneOfType([number, string])).isRequired,
  info: shape({}),
  setInfo: func.isRequired,
};

AuthorizationVolumes.defaultProps = {
  info: {},
};

export default AuthorizationVolumes;
